import { useMemo } from "react";
import API from "../Services/API";
import Model from "./Model";
import State from "./State";
import { toQueryString } from "../Services/helpers";
import { useToasts } from "../Components/Toasts";

const useQuotes = (searchData, alwaysRefresh) => {
    let searchStr = toQueryString(searchData)

    const data = Model({
        alwaysRefresh,
        stateKey: ['quotes', searchStr],
        fetchData: () => API.get('/quotes?'+searchStr),
        returnDataKey: 'quotes',
        saveAdditionalData: (response) => {
            response.map((quote) => {
                State.putIfNotExists(['quote', quote.id], quote);
                return quote;
            });
        },
        defaultLoadingError: 'Failed to load quotes.',
    });

    const groupedQuotes = useMemo(() => {
        if (!data.quotes) {
            return {
                newQuotes: [],
                laterQuotes: [],
                expectingQuotes: [],
                oldQuotes: [],
            };
        }

        return {
            newQuotes: data.quotes.filter(q => ['Processing', 'Submitted', 'Accepted', 'Declined', 'Purchased'].indexOf(q.status) !== -1),
            laterQuotes: data.quotes.filter(q => ['Expecting'].indexOf(q.status) !== -1),
            expectingQuotes: data.quotes.filter(q => ['Estimated', 'Itemised'].indexOf(q.status) !== -1),
            oldQuotes: data.quotes.filter(q => ['Closed', 'Rejected', 'Marketplace'].indexOf(q.status) !== -1),
        };
    }, [data]);

    return {
        ...data,
        ...groupedQuotes,
    }
}

const useQuote = (quoteId, alwaysRefresh = false) => {
    const data = Model({
        alwaysRefresh,
        stateKey: ['quote', quoteId],
        fetchData: () => API.get('/quotes/' + quoteId),
        returnDataKey: 'quote',
        registerAdditionalMethods: (opts) => {
            return {
                update: (data) => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}`, data),
                    'Quote updated',
                    true,
                ),
                updateEstimate: (data) => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/estimate`, data),
                    'Estimate saved',
                    true,
                ),
                createItemisation: (data) => opts.wrapUpdateCall(
                    () => API.post(`/pricing/itemisations?quoteId=${quoteId}&draft=1`, data),
                    'Cost breakdown created',
                    true,
                ),
                rejectQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/reject`),
                    'Quote was closed',
                    true,
                ),
                closeQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/close`),
                    'Quote was closed',
                    true,
                ),
                offerQuote: () => opts.wrapUpdateCall(
                    () => API.post(`/quotes/${quoteId}/offer`),
                    'Quote is now listed on the marketplace',
                    true,
                ),
                convert: (data) => opts.wrapUpdateCall(
                    () => {
                        const customerId = opts.model()?.quote?.customerId;
                        const path = customerId ? `/customers/${customerId}/updateFromQuote` : '/customers/createFromQuote';
                        return API.post(path, {quoteId, ...data});
                    },
                    'Quote converted to customer',
                    true,
                )
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading the quote.',
    });

    if (data?.quote?.quote) {
        data.quote = {
            ...data.quote.quote,
            customerUrl: data?.quote?.customerUrl ?? data?.customerUrl ?? null,
            area: data?.quote?.area ?? data?.area ?? null,
            distance: data?.quote?.distance ?? data?.distance ?? null,
            thread: data?.quote?.thread ?? data?.thread ?? null,
            itemisations: data?.quote?.itemisations ?? data?.itemisations ?? null,
        };
    }

    return data;
}

const useAddQuote = (data) => {
    const {showSuccess, showError} = useToasts();

    const addQuote = (data) => {
        return new Promise((resolve, reject) => {
            API.post('/quotes', data)
                .then((d) => {
                    showSuccess('Quote added');
                    resolve(d);
                })
                .catch((err) => {
                    showError(API.parseErrorResponse(err));
                    reject(err);
                })
            })
    }

    return {
        addQuote,
    };
}

export {
    useQuotes,
    useQuote,
    useAddQuote,
}
